import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberStepper } from "@/components/ui/number-stepper";
import type { InfluencerData } from "@/features/influencer-monitoring/store/influencerStore";
import { InfluencersTableHeader } from "./InfluencerTableHeader";
import { PLATFORM_KEYS } from "@/constants/platformData";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { useInfluencerTable } from "@/features/influencer-monitoring/hooks/useInfluencerTable";

interface InfluencersTableProps {
  data?: InfluencerData[];
  onTriggerMatch?: () => void;
  enabledPlatforms?: string[];
  isApiTokenLoading?: boolean;
  invalidLinks?: Set<string>;
  duplicateLinks?: Set<string>;
}

export default function InfluencersTable({
  data = [],
  enabledPlatforms = [],
  isApiTokenLoading = false,
  invalidLinks,
  duplicateLinks,
}: InfluencersTableProps) {
  const {
    rowCount,
    setRowCount,
    editingCell,
    editValue,
    sortColumn,
    sortDirection,
    footerWidth,
    scrollContainerRef,
    getSortedData,
    formatDisplayValue,
    handleSort,
    handleCellClick,
    handleInputChange,
    handleInputKeyDown,
    handleInputBlur,
    handleAddRows,
    isPlatformDisabled,
    isCellInvalidOrDuplicate,
  } = useInfluencerTable({
    data,
    enabledPlatforms,
    isApiTokenLoading,
    invalidLinks,
    duplicateLinks,
  });

  const renderCell = (
    rowIndex: number,
    platform: keyof InfluencerData,
    value: string,
  ) => {
    const isEditing =
      editingCell?.rowIndex === rowIndex && editingCell?.platform === platform;
    const isEmpty = value === "" || value === "N/A";
    const isDisabled = isPlatformDisabled(platform);
    const isInvalidOrDuplicate = isCellInvalidOrDuplicate(value);

    if (isEditing) {
      return (
        <Input
          value={editValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onBlur={handleInputBlur}
          className="h-auto min-h-[37px] w-full border-0 bg-transparent p-2 pl-3 text-sm font-medium text-neutral-950 shadow-none ring-0 outline-none focus:border-0 focus:bg-transparent focus:placeholder-transparent focus:shadow-none focus:ring-0 focus:outline-none"
          autoFocus
        />
      );
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`flex h-full w-full items-center p-2 text-sm font-medium ${
                isDisabled
                  ? "cursor-not-allowed text-neutral-300"
                  : isEmpty
                    ? "cursor-text text-neutral-600"
                    : isInvalidOrDuplicate
                      ? "cursor-text text-red-600"
                      : "cursor-text text-neutral-950"
              }`}
              onClick={() => {
                if (!isDisabled && !isApiTokenLoading) {
                  handleCellClick(rowIndex, platform, value);
                }
              }}
            >
              <div className="flex items-center gap-2">
                <div
                  className={`word-wrap flex-1 overflow-hidden leading-relaxed break-words hyphens-auto ${
                    isDisabled
                      ? "text-neutral-300"
                      : isEmpty
                        ? "text-neutral-400"
                        : isInvalidOrDuplicate
                          ? "text-red-600"
                          : "text-neutral-950"
                  }`}
                >
                  {isDisabled ? (
                    <p>Not Available</p>
                  ) : isEmpty ? (
                    <p>Enter Link</p>
                  ) : (
                    formatDisplayValue(value, platform)
                  )}
                </div>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {isDisabled
                ? "This platform is not available in your subscription"
                : isEmpty
                  ? "Click to add a link"
                  : isInvalidOrDuplicate
                    ? `${value}${invalidLinks?.has(value.trim()) ? " (invalid link)" : " (duplicate)"}`
                    : value}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const sortedData = getSortedData();

  return (
    <div className="flex h-full w-full flex-col rounded-[12px]">
      <TableContainer
        scrollRef={scrollContainerRef}
        className="relative min-h-[calc(98vh-260px)] flex-1"
      >
        <Table className="mb-[40px] h-full w-full">
          <InfluencersTableHeader
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            enabledPlatforms={enabledPlatforms}
          />
          <TableBody className="h-full pt-[40px]">
            {sortedData.map((row, index) => (
              <TableRow
                key={index}
                className="border-b border-neutral-200 transition-colors"
              >
                {PLATFORM_KEYS.map((platform) => (
                  <TableCell
                    key={platform}
                    className="max-h-[37px] max-w-[280px] min-w-[200px] overflow-hidden !p-0 !pl-1 align-top"
                  >
                    {renderCell(index, platform, row[platform] || "")}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div
          className="absolute right-0 bottom-0 left-0 flex h-[40px] items-center justify-between rounded-b-[12px] border-t border-neutral-200 bg-neutral-100 p-[6px] transition-all duration-200 ease-out"
          style={{ width: footerWidth }}
        >
          <div className="flex items-center gap-[2px]">
            <Button
              variant="ghost"
              className="flex items-center gap-[2px] text-sm text-neutral-600"
              onClick={handleAddRows}
            >
              Add
            </Button>
            <div className="flex items-center gap-2">
              <NumberStepper
                value={rowCount}
                onValueChange={setRowCount}
                min={1}
                max={10}
              />
              <span className="text-xs font-medium text-neutral-700">
                more rows
              </span>
            </div>
          </div>
        </div>
      </TableContainer>
    </div>
  );
}
